import React, { useEffect, useState, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { FilterProvider } from "./context/FilterContext";
import ChangePasswordModal from "./components/modals/ChangePasswordModal";
import Layout from "./components/common/Layout";
import "./App.css";
import { ToastProvider } from "./context/ToastContext";
import ToastNotification from "./components/common/ToastNotification";

// Enable Bootstrap icons & tooltips
import "bootstrap-icons/font/bootstrap-icons.css";
import { Tooltip } from "bootstrap";

// Lazy Loaded Routes
const Login = React.lazy(() => import("./pages/Login"));
const AdminPanel = React.lazy(() => import("./pages/AdminPanel/index"));
const Dashboard = React.lazy(() => import("./components/Dashboard/Dashboard"));
const NoVehicles = React.lazy(() => import("./pages/NoVehicles"));
const NotFound = React.lazy(() => import("./pages/NotFound"));
const RedirectToFirstVehicle = React.lazy(() => import("./components/RedirectToFirstVehicle"));
const RedirectToFirstVehicleExpenses = React.lazy(() => import("./components/RedirectToFirstVehicleExpenses"));
const RootRedirect = React.lazy(() => import("./components/RootRedirect"));
const ManageVehicles = React.lazy(() => import("./pages/ManageVehicles"));
const Expenses = React.lazy(() => import("./pages/Expenses"));

// Protected Route Wrapper
const PrivateRoute = ({ children }) => {
    const token = localStorage.getItem("token");
    return token ? children : <Navigate to="/login" replace />;
};

function App() {
    // Initialize Bootstrap tooltips inside a React hook
    useEffect(() => {
        const tooltips = document.querySelectorAll('[data-bs-toggle="tooltip"]');
        tooltips.forEach((tooltip) => {
            new Tooltip(tooltip);
        });
    }, []);

    const token = localStorage.getItem("token");
    let userRole;
    try {
        userRole = token ? JSON.parse(atob(token.split(".")[1])).role : null;
    } catch {
        userRole = null;
    }

    // Check mustResetPassword from localStorage
    const [forcePwModal, setForcePwModal] = useState(
        token && localStorage.getItem("mustResetPassword") === "true"
    );

    useEffect(() => {
        function checkMustReset() {
            const t = localStorage.getItem("token");
            setForcePwModal(t && localStorage.getItem("mustResetPassword") === "true");
        }
        window.addEventListener("mustResetPassword", checkMustReset);
        return () => window.removeEventListener("mustResetPassword", checkMustReset);
    }, []);

    const handlePwChanged = () => {
        localStorage.removeItem("mustResetPassword");
        setForcePwModal(false);
        window.location.reload(); // force reload to reset app state
    };

    return (
        <>
            {token && (
                <ChangePasswordModal
                    show={forcePwModal}
                    onHide={() => { }}
                    force
                    onChanged={handlePwChanged}
                />
            )}
            <ToastProvider>
                <FilterProvider>
                    <Router>
                        <Layout userRole={userRole}>
                            <Suspense fallback={
                                <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            }>
                                <Routes>
                                    {/* Handle root route */}
                                    <Route path="/" element={<RootRedirect />} />

                                    {/* Login route */}
                                    <Route path="/login" element={<Login />} />

                                    {/* Automatically redirect /dashboard to the first vehicle */}
                                    <Route
                                        path="/dashboard"
                                        element={
                                            <PrivateRoute>
                                                <RedirectToFirstVehicle />
                                            </PrivateRoute>
                                        }
                                    />

                                    {/* Protected route for specific vehicle dashboards */}
                                    <Route
                                        path="/dashboard/:vehicleId"
                                        element={
                                            <PrivateRoute>
                                                <Dashboard />
                                            </PrivateRoute>
                                        }
                                    />

                                    {/* Expenses routes */}
                                    <Route
                                        path="/expenses"
                                        element={
                                            <PrivateRoute>
                                                <RedirectToFirstVehicleExpenses />
                                            </PrivateRoute>
                                        }
                                    />
                                    <Route
                                        path="/expenses/:vehicleId"
                                        element={
                                            <PrivateRoute>
                                                <Expenses />
                                            </PrivateRoute>
                                        }
                                    />

                                    {/* No Vehicles screen */}
                                    <Route
                                        path="/no-vehicles"
                                        element={
                                            <PrivateRoute>
                                                <NoVehicles />
                                            </PrivateRoute>
                                        }
                                    />

                                    <Route
                                        path="/manage-vehicles"
                                        element={
                                            <PrivateRoute>
                                                <ManageVehicles />
                                            </PrivateRoute>
                                        }
                                    />

                                    <Route path="/admin" element={<AdminPanel />} />

                                    {/* Catch-all for 404 Not Found */}
                                    <Route path="*" element={<NotFound />} />
                                </Routes>
                            </Suspense>
                        </Layout>
                    </Router>
                </FilterProvider>
                <ToastNotification />
            </ToastProvider>
        </>
    );
}

export default App;
